import './client-otp-modal.scss';

import { Alert, Drawer, Modal } from 'antd';
import { useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';

import AlertCircleIcon from '@/media/icons/alert-circle.svg?react';
import { formatPhoneNumber } from '@/services/formatter';

import { Button } from '../../ui';
import useClientForm from './hooks/useClientForm';
import useOtpForm from './hooks/useOtpForm';
import type { ClientFormValues } from './types/client-form';
import type { ModalOpenerType } from './types/general';
import type { OtpFormValues } from './types/otp-form';
import { BmgErrorModal, ClientForm, Loader, OtpForm, Timer } from './ui';

interface IProps {
  title: string;
  subtitle?: string;
  onClientSubmit: (values: ClientFormValues) => Promise<string>;
  onOtpSubmit: (values: OtpFormValues) => Promise<string>;
  onWhatsappRetry?: (phone: string) => void;
  onSMSRetry: (phone: string) => void;
  openedModal: ModalOpenerType;
  setOpenedModal: (modal: ModalOpenerType) => void;
  onClose: () => void;
  defaultPhone?: string;
}

const ClientOtpModal: React.FC<IProps> = ({
  title,
  subtitle,
  onClientSubmit,
  onOtpSubmit,
  openedModal,
  setOpenedModal,
  onClose,
  onWhatsappRetry,
  onSMSRetry,
  defaultPhone
}) => {
  const [openCommentsDrawer, setOpenCommentsDrawer] = useState(false);

  const {
    clientComments,
    clientError,
    clientFormLoading,
    clientFormMethods,
    clientSecurity,
    handleClientFormSubmit,
    resetClientComments,
    searchClientLoading
  } = useClientForm({
    submitRequest: onClientSubmit,
    handleNext: () => setOpenedModal('otp-form')
  });
  const phone = useWatch({ control: clientFormMethods.control, name: 'phone' });

  const handleClose = () => {
    onClose();
    setOpenedModal('');
    setOpenCommentsDrawer(false);
    resetOtpError();
  };

  const {
    otpFormMethods,
    handleOtpFormSubmit,
    otpLoading,
    otpError,
    resetOtpError
  } = useOtpForm({
    submitRequest: onOtpSubmit,
    onClose: handleClose
  });

  useEffect(() => {
    if (openedModal === '' || openedModal === 'bmg-error') {
      clientFormMethods.reset({
        iin: '',
        fullName: '',
        phone: ''
      });
    }
    if (openedModal === '' || openedModal === 'client-info-form') {
      otpFormMethods.reset({
        first: undefined,
        second: undefined,
        third: undefined,
        fourth: undefined
      });
      resetClientComments();
    }
  }, [openedModal]);

  useEffect(() => {
    if (openedModal === 'client-info-form') {
      clientFormMethods.clearErrors(['iin', 'fullName', 'phone']);
      // Фокусим после открытия модалки, иначе событие теряется
      setTimeout(() => clientFormMethods.setFocus('iin'), 200);
    } else if (openedModal === 'otp-form') {
      otpFormMethods.clearErrors(['first', 'second', 'third', 'fourth']);
      // Фокусим после открытия модалки, иначе событие теряется
      setTimeout(() => otpFormMethods.setFocus('first'), 200);
    }
  }, [openedModal]);

  return (
    <>
      <Modal
        centered
        className="client-otp-modal"
        title={title}
        open={openedModal === 'client-info-form'}
        onCancel={handleClose}
        mask={{ closable: true }}
        footer={null}
      >
        {subtitle && <p className="client-otp-modal__subtitle">{subtitle}</p>}

        <ClientForm
          clientComments={clientComments}
          clientError={clientError}
          clientFormLoading={clientFormLoading}
          clientSecurity={clientSecurity}
          handleClientFormSubmit={handleClientFormSubmit}
          handleClose={handleClose}
          hasWhatsApp={onWhatsappRetry !== undefined}
          methods={clientFormMethods}
          onOpenComments={() => setOpenCommentsDrawer(true)}
          searchClientLoading={searchClientLoading}
        />
      </Modal>

      <BmgErrorModal
        isOpened={openedModal === 'bmg-error'}
        onCancel={() => setOpenedModal('')}
        onRetry={() => setOpenedModal('client-info-form')}
      />

      <Drawer
        title="Комментарии"
        open={openCommentsDrawer}
        onClose={() => setOpenCommentsDrawer(false)}
      >
        <div className="client-otp-modal__comments">
          {clientComments.map((comment, index) => (
            <Alert
              title={comment}
              key={comment.slice(0, 10) + index}
              type="warning"
              showIcon
            />
          ))}
        </div>
      </Drawer>

      <Modal
        centered
        className="client-otp-modal"
        title="Введите код подтверждения"
        open={openedModal === 'otp-form'}
        destroyOnHidden
        onCancel={handleClose}
        mask={{ closable: false }}
        footer={null}
      >
        <p className="client-otp-modal__subtitle">
          Код подтверждения отправлен на номер
        </p>
        <p className="client-otp-modal__phone">
          {formatPhoneNumber(phone || defaultPhone)}
        </p>

        <div className="client-otp-modal__form">
          <OtpForm
            resetOtpError={resetOtpError}
            methods={otpFormMethods}
            onSubmit={handleOtpFormSubmit}
          />
        </div>
        {otpError && (
          <p className="client-otp-modal__error">
            <AlertCircleIcon />
            {otpError}
          </p>
        )}

        <div
          className={`client-otp-modal__otp-loading ${otpLoading ? 'client-otp-modal__otp-loading--active' : ''}`}
        >
          <Loader />
          <p>Подождите...</p>
        </div>
        <div
          className={`client-otp-modal__timer ${!otpLoading ? 'client-otp-modal__timer--active' : ''}`}
        >
          <Timer
            timerStarted={openedModal === 'otp-form'}
            onRetry={(type) =>
              type === 'phone'
                ? onSMSRetry(phone || defaultPhone || '')
                : onWhatsappRetry?.(phone || defaultPhone || '')
            }
            hasWhatsApp={onWhatsappRetry !== undefined}
            isContinuing={!defaultPhone}
          />
        </div>

        {!otpLoading && (
          <Button onClick={handleClose} className="client-otp-modal__cancel">
            Отмена
          </Button>
        )}
      </Modal>
    </>
  );
};

export default ClientOtpModal;
